import { useState } from "react";
import type { HoursOfOperationDetail, HoursSchedule, ProfileField } from "@starsach/shared-types";
import { Card, DataTable, EmptyState, btnPrimary, btnSecondary, inputClass } from "../../../components/ui";
import { AnnouncementRef } from "../../customer/ProfileFieldEditors";
import { TabMenu, type CsvShape } from "./TabMenu";

type HoursField = Extract<ProfileField, { type: "hours" }>;
type Holiday = NonNullable<HoursSchedule["holidays"]>[number];

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

const holidayShape = (announcements: string[]): CsvShape<Holiday> => ({
  name: "holidays",
  headers: ["date", "name", "closed", "opens", "closes", "announcement"],
  example: [
    { date: "2026-12-25", name: "Christmas Day", closed: true, announcement: announcements[0] ?? "closed_christmas" },
    { date: "2026-12-24", name: "Christmas Eve", closed: false, opens: "09:00", closes: "12:00" },
  ],
  toRow: (h) => ({ date: h.date, name: h.name, closed: h.closed ? "yes" : "no", opens: h.opens ?? "", closes: h.closes ?? "", announcement: h.announcement ?? "" }),
  fromRow: (r, line) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(r["date"] ?? "")) return `Line ${line}: date must be YYYY-MM-DD (got "${r["date"]}").`;
    if (!r["name"]) return `Line ${line}: a holiday needs a name.`;
    const closed = /^(yes|true|1|closed|y)$/i.test(r["closed"] ?? "");
    if (!closed && (!TIME.test(r["opens"] ?? "") || !TIME.test(r["closes"] ?? ""))) return `Line ${line}: a holiday that is not closed needs opens and closes as HH:MM.`;
    return { date: r["date"]!, name: r["name"]!, closed, ...(closed ? {} : { opens: r["opens"]!, closes: r["closes"]! }), ...(r["announcement"] ? { announcement: r["announcement"] } : {}) };
  },
});

/**
 * Holidays, their own tab (the owner's ask of 2026-09-18): one row per
 * date, closed all day or open for part of it, each with the announcement
 * it plays - one per holiday, chosen from the profile's announcements, so
 * "we are closed for Christmas" is Christmas's and nothing else's.
 */
export function HolidaysTab({ fields, announcements, connectOverrides, onUpdate }: { fields: HoursField[]; announcements: string[]; /** ADR-0019: the overrides Connect holds on each linked hours of operation, by schedule name - a source to copy from. */ connectOverrides?: Record<string, HoursOfOperationDetail["overrides"]>; onUpdate: (field: ProfileField, next: ProfileField) => void }) {
  const [which, setWhich] = useState(fields[0]?.name);
  const field = fields.find((f) => f.name === which) ?? fields[0];
  const [adding, setAdding] = useState(false);
  const [copyFrom, setCopyFrom] = useState("");
  if (!field) {
    return (
      <Card title="Holidays">
        <EmptyState title="No hours of operation yet" hint="Holidays belong to a schedule. Add hours on the Hours tab first, then the holidays that override them." />
      </Card>
    );
  }
  const holidays = field.value.holidays ?? [];
  const setHolidays = (next: Holiday[]) => onUpdate(field, { ...field, value: { ...field.value, holidays: next } });
  const update = (i: number, patch: Partial<Holiday>) => setHolidays(holidays.map((h, j) => (j === i ? { ...h, ...patch } : h)));
  const shape = holidayShape(announcements);
  // Where holidays can be copied from (the owner's ask of 2026-09-18: most hours of operation share them): another
  // schedule on this profile, or what Connect holds on this schedule's hours of operation - single-day overrides only.
  const sources: Array<{ id: string; label: string; holidays: Holiday[] }> = [
    ...fields.filter((f) => f.name !== field.name && (f.value.holidays?.length ?? 0) > 0).map((f) => ({ id: `schedule:${f.name}`, label: `schedule "${f.name}" (${f.value.holidays!.length})`, holidays: f.value.holidays! })),
    ...(field.value.connect_hours && connectOverrides?.[field.name]
      ? [{
          id: "connect",
          label: `Connect's overrides on ${field.value.connect_hours} (${connectOverrides[field.name]!.filter((o) => o.from === o.till).length})`,
          holidays: connectOverrides[field.name]!
            .filter((o) => o.from === o.till)
            .map((o) => {
              const entry = o.config[0];
              return { date: o.from, name: o.name.replace(/^starsach:[^:]*:/, ""), closed: !entry, ...(entry ? { opens: entry.start, closes: entry.end === "00:00" ? "23:59" : entry.end } : {}) };
            }),
        }]
      : []),
  ];
  const copy = () => {
    const source = sources.find((s) => s.id === copyFrom);
    if (!source) return;
    const byDate = new Map(holidays.map((h) => [h.date, h]));
    for (const h of source.holidays) if (!byDate.has(h.date)) byDate.set(h.date, h);
    setHolidays([...byDate.values()].sort((a, b) => a.date.localeCompare(b.date)));
    setCopyFrom("");
  };

  return (
    <Card title={fields.length > 1 ? `Holidays · ${field.name}` : "Holidays"}>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {fields.length > 1 && (
          <label className="text-sm">
            <span className="mr-2 text-neutral-500">Schedule</span>
            <select className={`${inputClass} mt-0 w-auto`} value={field.name} onChange={(e) => setWhich(e.target.value)} aria-label="Which hours">
              {fields.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
            </select>
          </label>
        )}
        <button type="button" className={btnSecondary} onClick={() => setAdding((a) => !a)} aria-expanded={adding}>Add holiday</button>
        {sources.length > 0 && (
          <span className="flex items-center gap-2 text-sm">
            <select className={`${inputClass} mt-0 w-auto`} value={copyFrom} aria-label="Copy holidays from" onChange={(e) => setCopyFrom(e.target.value)}>
              <option value="">Copy holidays from…</option>
              {sources.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <button type="button" className={`${btnSecondary} min-h-8 px-2.5 text-xs`} disabled={!copyFrom} onClick={copy}>Copy</button>
          </span>
        )}
        <span className="ml-auto">
          <TabMenu
            what="Holidays"
            shape={shape}
            items={holidays}
            onImport={(items, replace) => {
              const byDate = new Map((replace ? [] : holidays).map((h) => [h.date, h]));
              for (const h of items) byDate.set(h.date, h);
              setHolidays([...byDate.values()].sort((a, b) => a.date.localeCompare(b.date)));
            }}
            onDeleteAll={() => setHolidays([])}
          />
        </span>
      </div>
      {adding && (
        <NewHoliday
          announcements={announcements}
          onAdd={(h) => {
            setHolidays([...holidays, h].sort((a, b) => a.date.localeCompare(b.date)));
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
      <DataTable
        caption={`Holidays on ${field.name}`}
        rows={holidays.map((h, i) => ({ ...h, i }))}
        rowKey={(h) => `${h.date}#${h.i}`}
        filter={holidays.length > 5}
        empty={<EmptyState title="No holidays yet" hint="Add one, or import a CSV - the example file shows the shape." />}
        columns={[
          { key: "date", header: "Date", render: (h) => <input type="date" className={`${inputClass} mt-0 w-40`} value={h.date} aria-label={`Holiday ${h.i + 1} date`} onChange={(e) => update(h.i, { date: e.target.value })} /> },
          { key: "name", header: "Name", render: (h) => <input className={`${inputClass} mt-0 w-44`} value={h.name} aria-label={`Holiday ${h.i + 1} name`} onChange={(e) => update(h.i, { name: e.target.value })} /> },
          {
            key: "status",
            header: "Status",
            render: (h) => (
              <span className="flex flex-wrap items-center gap-2 text-xs">
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={h.closed} aria-label={`Holiday ${h.i + 1} closed all day`} onChange={(e) => update(h.i, e.target.checked ? { closed: true, opens: undefined, closes: undefined } : { closed: false, opens: h.opens ?? "09:00", closes: h.closes ?? "12:00" })} />
                  Closed all day
                </label>
                {!h.closed && (
                  <>
                    <input type="time" className={`${inputClass} mt-0 w-28`} value={h.opens ?? ""} aria-label={`Holiday ${h.i + 1} opens`} onChange={(e) => update(h.i, { opens: e.target.value })} />
                    <span>–</span>
                    <input type="time" className={`${inputClass} mt-0 w-28`} value={h.closes ?? ""} aria-label={`Holiday ${h.i + 1} closes`} onChange={(e) => update(h.i, { closes: e.target.value })} />
                  </>
                )}
              </span>
            ),
          },
          { key: "announcement", header: "Announcement", render: (h) => <span className="block w-56"><AnnouncementRef value={h.announcement ?? ""} announcements={announcements} label={`Holiday ${h.i + 1} announcement`} onChange={(a) => update(h.i, { announcement: a || undefined })} /></span> },
          { key: "remove", header: "", render: (h) => <button type="button" className="text-xs text-red-700 underline dark:text-red-300" onClick={() => setHolidays(holidays.filter((_, j) => j !== h.i))}>Remove</button> },
        ]}
      />
      <p className="mt-3 text-xs text-neutral-500">A holiday with no announcement of its own plays the schedule's "say when closed" outside its hours.{field.value.connect_hours ? ` Each holiday is written into Connect's ${field.value.connect_hours} as an override named starsach:… on the next sync.` : ""}</p>
    </Card>
  );
}

function NewHoliday({ announcements, onAdd, onCancel }: { announcements: string[]; onAdd: (h: Holiday) => void; onCancel: () => void }) {
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [closed, setClosed] = useState(true);
  const [opens, setOpens] = useState("09:00");
  const [closes, setCloses] = useState("12:00");
  const [announcement, setAnnouncement] = useState("");
  return (
    <form
      className="mb-4 flex flex-wrap items-end gap-3 rounded border border-dashed border-neutral-300 p-3 dark:border-neutral-700"
      aria-label="New holiday"
      onSubmit={(e) => {
        e.preventDefault();
        if (!date || !name.trim()) return;
        onAdd({ date, name: name.trim(), closed, ...(closed ? {} : { opens, closes }), ...(announcement ? { announcement } : {}) });
      }}
    >
      <label className="text-xs">Date<input type="date" className={`${inputClass} w-40`} value={date} required onChange={(e) => setDate(e.target.value)} /></label>
      <label className="text-xs">Name<input className={`${inputClass} w-48`} value={name} required placeholder="Christmas Day" onChange={(e) => setName(e.target.value)} /></label>
      <label className="flex items-center gap-1 pb-2 text-xs"><input type="checkbox" checked={closed} onChange={(e) => setClosed(e.target.checked)} />Closed all day</label>
      {!closed && (
        <>
          <label className="text-xs">Opens<input type="time" className={`${inputClass} w-28`} value={opens} onChange={(e) => setOpens(e.target.value)} /></label>
          <label className="text-xs">Closes<input type="time" className={`${inputClass} w-28`} value={closes} onChange={(e) => setCloses(e.target.value)} /></label>
        </>
      )}
      <span className="text-xs">Announcement<span className="block w-56"><AnnouncementRef value={announcement} announcements={announcements} label="New holiday announcement" onChange={setAnnouncement} /></span></span>
      <button type="submit" className={btnPrimary}>Add</button>
      <button type="button" className={btnSecondary} onClick={onCancel}>Cancel</button>
    </form>
  );
}
